import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CustomOrderEntry } from '@cart/const';
import { CartUndoService } from '@cart/feature';
import { PriceKind, SelectedPrice } from '@product/const';
import { ProductItemCounterComponent } from '@sdvor/product/ui/v2';
import { CopyCodeComponent, SwipeXDirective } from '@sdvor/shared/ui/v2';
import { DeviceService } from '@shared/services';
import { IconTypes } from '@shared/v2';
import { UrlModule, WindowRef } from '@spartacus/core';
import { fromEvent } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { CartCrossButtonComponent, CartWishlistButtonComponent } from './cart-list-item-actions';
import { CartListItemAvailabilityComponent } from './cart-list-item-availability';
import { CartListItemGiftComponent } from './cart-list-item-gift';
import { CartListItemImageComponent } from './cart-list-item-image';
import { CartListItemMobileDeleteComponent } from './cart-list-item-mobile-delete';
import { CartListItemPriceComponent } from './cart-list-item-price';

const IS_ALREADY_DELETE_SWIPE = 'isAlreadyDeleteSwipe';

@Component({
  selector: 'sd-cart-list-item',
  imports: [
    UrlModule,
    RouterLink,
    SwipeXDirective,
    CopyCodeComponent,
    ReactiveFormsModule,
    CartCrossButtonComponent,
    CartListItemGiftComponent,
    CartListItemImageComponent,
    CartListItemPriceComponent,
    ProductItemCounterComponent,
    CartWishlistButtonComponent,
    CartListItemAvailabilityComponent,
    CartListItemMobileDeleteComponent,
  ],
  templateUrl: './cart-list-item.component.html',
  styleUrls: ['./cart-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartListItemComponent {
  private winRef = inject(WindowRef);
  private destroyRef = inject(DestroyRef);
  private deviceService = inject(DeviceService);
  private cartUndoService = inject(CartUndoService);

  protected readonly isLastItem = input.required<boolean>();
  protected readonly isFirstItem = input.required<boolean>();
  protected readonly entry = input.required<CustomOrderEntry>();
  protected readonly quantityControl = input.required<FormControl>();

  protected readonly isMobileSmDown = toSignal(this.deviceService.isMobileSmDown$, { initialValue: false });
  protected readonly isTabletMdDown = toSignal(this.deviceService.isTabletMdDown$, { initialValue: false });
  protected readonly isTabletMdUp = toSignal(this.deviceService.isTabletMdUp$, { initialValue: false });
  protected readonly iconTypes = IconTypes;
  protected readonly isGifted = computed(() => this.entry().basePrice?.value === 0);
  protected readonly selectedPrice = computed(() => {
    const item = this.entry();

    return {
      ...item.basePrice,
      unit: item.saleUnit,
      saleUnit: item.saleUnit,
      kind: item.transit ? PriceKind.TRANSIT : PriceKind.REGULAR,
    } as SelectedPrice;
  });

  protected readonly swipeOffset = signal(0);
  protected readonly showDeleteButton = computed(() => this.swipeOffset() <= -50);
  private animationPlayed = false;

  constructor() {
    if (this.winRef.nativeWindow) {
      fromEvent(this.winRef.nativeWindow, 'scroll')
        .pipe(throttleTime(100), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.resetSwipe());
    }

    effect(() => {
      if (!this.isMobileSmDown()) {
        this.resetSwipe();
      }
    });

    effect(() => {
      const isAlreadyDeleteSwipe = this.winRef.localStorage?.getItem(IS_ALREADY_DELETE_SWIPE);

      if (isAlreadyDeleteSwipe) {
        this.animationPlayed = true;
      }

      if (this.isFirstItem() && !this.animationPlayed && this.isMobileSmDown() && !this.isGifted()) {
        this.animationPlayed = true;
        setTimeout(() => {
          this.runSwipeAnimation();
        }, 500);
      }
    });
  }

  private runSwipeAnimation(): void {
    this.swipeOffset.set(-100);

    setTimeout(() => {
      this.swipeOffset.set(0);
    }, 1500);
  }

  onSwipeMove(offset: number): void {
    this.swipeOffset.set(offset);
  }

  onSwipeEnd(passed: boolean): void {
    if (passed) {
      this.swipeOffset.set(-100);
    } else {
      this.resetSwipe();
    }
  }

  onSwipeDirection(direction: 'left' | 'right'): void {
    if (direction === 'right') {
      this.resetSwipe();
    }
  }

  resetSwipe(): void {
    try {
      this.swipeOffset.set(0);
    } catch (error) {}
  }

  removeItem(isSwipeDelete = false): void {
    this.resetSwipe();

    this.cartUndoService.registerUndo(this.entry());

    if (isSwipeDelete) {
      this.winRef.localStorage?.setItem(IS_ALREADY_DELETE_SWIPE, String(true));
    }
    this.quantityControl().setValue(0);
    this.quantityControl().markAsDirty();
  }
}
